import { Injectable } from '@angular/core';
import { MicroappMessagingService } from '@core/messaging/microapp-messaging.service';
import { EmbedModeService } from '@core/runtime/embed-mode.service';

@Injectable({ providedIn: 'root' })
export class AutoResizeService {
  private observer?: ResizeObserver;
  private rafId: number | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastHeight = 0;
  private throttleMs = 120;
  private started = false;

  constructor(
    private readonly messaging: MicroappMessagingService,
    private readonly embedMode: EmbedModeService
  ) {}

  start(throttleMs = 120): void {
    if (this.started || !this.embedMode.isEmbedded()) {
      return;
    }

    this.started = true;
    this.throttleMs = throttleMs;
    this.lastHeight = 0;

    this.observer = new ResizeObserver(() => this.scheduleHeightPost());
    this.observer.observe(document.body);
    this.postHeight();
  }

  stop(): void {
    this.started = false;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private scheduleHeightPost(): void {
    if (!this.started) {
      return;
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.timeoutId = setTimeout(() => {
        this.timeoutId = null;
        this.postHeight();
      }, this.throttleMs);
    });
  }

  private postHeight(): void {
    if (!this.started) {
      return;
    }

    const height = document.body.scrollHeight;
    if (Math.abs(height - this.lastHeight) <= 2) {
      return;
    }

    this.lastHeight = height;
    this.messaging.notifyResize(height);
  }
}
